import { Moment } from 'moment';
import { UserExam } from '../types/User';
import axiosInstance from './axiosCreator';
import { store } from '../store/store';
import {
  examsFailed,
  examsLoading,
  examsReceived,
  examStatsReceived,
  examCreated,
  examUpdated,
  examsSelectors,
  customExamsReordered,
  setPrimaryExam,
} from '../slices/exams/examsSlice';
import { STEP1_EXAM_ID } from '../utils/examUtils';

// Fetch exams
let inFlightExamsFetch: Promise<UserExam[]> | null = null;
let inFlightExamMetadataFetch: Promise<UserExam[]> | null = null;
let inFlightExamStatsFetch: Promise<ExamStats[]> | null = null;

type ExamStats = Pick<UserExam, 'id'> &
  Partial<
    Pick<UserExam, 'lectureCount' | 'videos' | 'flashcards' | 'vignettes'>
  >;

const ensureNonFreeStudyPrimary = async (
  exams: UserExam[],
  refreshPath = '/exams'
): Promise<UserExam[]> => {
  const currentPrimary = exams.find((exam) => exam.primary);
  if (!currentPrimary || currentPrimary.type !== 'FREE_STUDY') {
    return exams;
  }

  const fallbackExam =
    exams.find(
      (exam) => exam.id === STEP1_EXAM_ID && exam.type !== 'FREE_STUDY'
    ) ?? exams.find((exam) => exam.type !== 'FREE_STUDY');

  if (!fallbackExam) {
    return exams;
  }

  const fallbackState = exams.map((exam) => ({
    ...exam,
    primary: exam.id === fallbackExam.id,
  }));

  const state = store.getState() as {
    auth?: { userId?: string | null };
    user?: { profile?: { id?: string | null } | null };
  };

  const userId = state.auth?.userId ?? state.user?.profile?.id ?? null;
  if (!userId) {
    return fallbackState;
  }

  try {
    await axiosInstance.patch(`/users/${userId}/exams/${fallbackExam.id}`, {
      primary: true,
    });

    const { data: refreshedResponse } = await axiosInstance.get(refreshPath);
    if (refreshedResponse?.success && Array.isArray(refreshedResponse.data)) {
      return refreshedResponse.data as UserExam[];
    }
  } catch (error) {
    console.error('Failed to normalize FREE_STUDY primary exam:', error);
  }

  return fallbackState;
};

export const fetchExams = async (options?: { minFreshMs?: number }) => {
  const minFreshMs = options?.minFreshMs ?? 0;

  const state = store.getState() as {
    exams?: { status?: string; lastFetchedAt?: number | null };
  };

  const examsState = state.exams;
  const lastFetchedAt = examsState?.lastFetchedAt ?? null;
  if (
    minFreshMs > 0 &&
    examsState?.status === 'succeeded' &&
    lastFetchedAt &&
    Date.now() - lastFetchedAt < minFreshMs
  ) {
    return examsSelectors.selectExams(state as never) as UserExam[];
  }

  if (inFlightExamsFetch) {
    return inFlightExamsFetch;
  }

  inFlightExamsFetch = (async () => {
    try {
      store.dispatch(examsLoading());
      const { data: response } = await axiosInstance.get('/exams');
      if (!response.success) {
        store.dispatch(examsFailed(response.error));
        throw new Error(response.error);
      }
      const normalizedExams = await ensureNonFreeStudyPrimary(response.data);
      // Dispatch the exams to the store
      store.dispatch(examsReceived(normalizedExams));
      return normalizedExams;
    } catch (error) {
      console.error('Error fetching exams:', error);
      throw error;
    } finally {
      inFlightExamsFetch = null;
    }
  })();

  return inFlightExamsFetch;
};

export const fetchExamMetadata = async (options?: { minFreshMs?: number }) => {
  const minFreshMs = options?.minFreshMs ?? 0;

  const state = store.getState() as {
    exams?: { status?: string; lastFetchedAt?: number | null };
  };

  const examsState = state.exams;
  const lastFetchedAt = examsState?.lastFetchedAt ?? null;
  if (
    minFreshMs > 0 &&
    examsState?.status === 'succeeded' &&
    lastFetchedAt &&
    Date.now() - lastFetchedAt < minFreshMs
  ) {
    return examsSelectors.selectExams(state as never) as UserExam[];
  }

  if (inFlightExamMetadataFetch) {
    return inFlightExamMetadataFetch;
  }

  inFlightExamMetadataFetch = (async () => {
    try {
      store.dispatch(examsLoading());
      const { data: response } = await axiosInstance.get('/exams/metadata');
      if (!response.success) {
        throw new Error(response.error);
      }

      const normalizedExams = await ensureNonFreeStudyPrimary(
        response.data,
        '/exams/metadata'
      );

      store.dispatch(examsReceived(normalizedExams));
      return normalizedExams;
    } catch (error) {
      console.error('Error fetching exam metadata:', error);

      // Every failure settles the status it set to 'loading' (a 403 for an
      // admin without 2FA included): a caller that rethrows or ignores the
      // error must not leave the route guard on its spinner.
      store.dispatch(
        examsFailed(
          error instanceof Error
            ? error.message
            : 'Failed to fetch exam metadata'
        )
      );

      throw error;
    } finally {
      inFlightExamMetadataFetch = null;
    }
  })();

  return inFlightExamMetadataFetch;
};

export const fetchExamStats = async () => {
  if (inFlightExamStatsFetch) {
    return inFlightExamStatsFetch;
  }

  inFlightExamStatsFetch = (async () => {
    try {
      const { data: response } = await axiosInstance.get('/exams/stats');
      if (!response.success) {
        throw new Error(response.error);
      }

      const stats = response.data as ExamStats[];
      store.dispatch(examStatsReceived(stats));
      return stats;
    } catch (error) {
      console.error('Error fetching exam stats:', error);
      throw error;
    } finally {
      inFlightExamStatsFetch = null;
    }
  })();

  return inFlightExamStatsFetch;
};

export interface ExamSocialStats {
  examId: string;
  upvoteCount: number;
  studierCount: number;
}

// Display-only social stats (upvotes + distinct studiers) for a set of exams,
// fetched lazily when the School & Shared Exams chooser is shown. Deliberately
// separate from /exams/metadata so those counts never slow the critical-path
// exam list. Returns an empty list on failure so the UI can fall back to 0s.
export const fetchExamSocialStats = async (
  examIds: string[]
): Promise<ExamSocialStats[]> => {
  if (examIds.length === 0) {
    return [];
  }

  const { data: response } = await axiosInstance.post('/exams/social-stats', {
    examIds,
  });

  if (!response?.success) {
    throw new Error(response?.error ?? 'Failed to fetch exam social stats');
  }

  return response.data as ExamSocialStats[];
};

export const reorderCustomExams = async (examIds: string[]) => {
  const previousExams = examsSelectors.selectExams(
    store.getState() as never
  ) as UserExam[];

  store.dispatch(customExamsReordered(examIds));

  try {
    const response = await axiosInstance.patch('/exams/custom-order', {
      examIds,
    });

    if (!response.data.success) {
      store.dispatch(examsReceived(previousExams));
      throw new Error(response.data.error);
    }

    if (Array.isArray(response.data.data?.examIds)) {
      store.dispatch(customExamsReordered(response.data.data.examIds));
    }

    return response.data;
  } catch (error) {
    store.dispatch(examsReceived(previousExams));
    console.error('Failed to reorder custom exams:', error);
    return {
      success: false,
      error: 'Failed to reorder custom exams',
    };
  }
};

export const createSchoolExam = async (
  name: string,
  date: Moment | null,
  schoolId?: string
): Promise<UserExam | undefined> => {
  try {
    store.dispatch(examsLoading());

    const response = await axiosInstance.post('/exams', {
      name,
      schoolId: schoolId || null,
      date,
    });

    if (!response.data.success) {
      store.dispatch(examsFailed(response.data.error));
      throw new Error('Failed to create school exam');
    }

    const createdExam = response.data.data;
    store.dispatch(examCreated(createdExam));

    if (createdExam?.type === 'CUSTOM' && createdExam.isActiveCustomExam) {
      const preclinicalExam = examsSelectors.selectPreclinicalExam(
        store.getState() as never
      );

      if (preclinicalExam?.id) {
        store.dispatch(setPrimaryExam(preclinicalExam.id));
      } else {
        await fetchExams();
      }
    }

    return createdExam;
  } catch (error) {
    console.error('Failed to create school exam:', error);
    // A create that threw before the server-error branch (e.g. a network
    // failure) would otherwise leave exams.status stuck on 'loading' from the
    // examsLoading() above — which wedges the preclinical home on its
    // resolution skeleton (isResolvingActiveCustomExam keys on 'loading'),
    // blocking a retry. Reset to 'failed', preserving a server error if one
    // was already recorded by the !success branch.
    if (store.getState().exams.status !== 'failed') {
      store.dispatch(examsFailed('Failed to create exam'));
    }
    return undefined;
  }
};

export const deleteCustomExam = async (examId: string) => {
  try {
    const response = await axiosInstance.delete(`/exams/${examId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting custom exam:', error);
    return {
      success: false,
      error: 'Failed to delete exam',
    };
  }
};

// The school-email domains that unlock this exam's shared documents (for the
// verify-school-email modal). Returns [] on any failure so the caller can fall
// back to the server's own domain check on submit.
export const fetchExamVerificationDomains = async (
  examId: string
): Promise<string[]> => {
  try {
    const response = await axiosInstance.get(
      `/exams/${examId}/verification-domains`
    );

    const domains = response.data?.data?.domains;
    return Array.isArray(domains) ? domains : [];
  } catch (error) {
    console.error('Error fetching exam verification domains:', error);
    return [];
  }
};

/**
 * Updates exam sharing settings including verification status and school sharing
 * @param examId - The ID of the exam to update
 * @param verified - Whether the exam should be marked as verified
 * @param sharedWithSchool - Whether the exam should be shared with the school
 * @param schoolId - The ID of the school to share with
 * @param shouldDispatch - Whether to dispatch the update to the store
 * @returns The updated exam data
 * @throws Error when the update fails
 */
export const updateExamSharing = async ({
  examId,
  verified,
  sharedWithSchool,
  schoolId,
  shouldDispatch = true,
}: {
  examId: string;
  verified?: boolean;
  sharedWithSchool?: boolean;
  schoolId?: string;
  shouldDispatch?: boolean;
}) => {
  try {
    const response = await axiosInstance.put(`/exams/${examId}/share`, {
      verified,
      sharedWithSchool,
      schoolId,
    });

    if (!response.data?.success) {
      const errorMessage =
        response.data?.error || 'Failed to update exam sharing settings';

      throw new Error(errorMessage);
    }

    // Only dispatch to store if requested and we have valid response data
    if (shouldDispatch && response.data) {
      store.dispatch(
        examUpdated({
          id: examId,
          verified: response.data.data.verified,
          sharedWithSchool: response.data.data.sharedWithSchool,
          schoolId: response.data.data.schoolId,
          // Sharing enrolls (and unsharing removes) every school user in a
          // server-side cascade that outlives this response, so neither the
          // stored count nor the one in the response is trustworthy now.
          // Clear it so Delete is only ever disabled on a fresh server count.
          memberCount: undefined,
        })
      );
    }

    return response.data.data;
  } catch (error) {
    console.error('Unexpected error while updating exam sharing:', error);
    throw error;
  }
};

/**
 * Requests an AI-generated exam name from the backend for a given exam.
 * Returns the generated name string, or undefined if the request fails.
 */
export const generateExamName = async (
  examId: string
): Promise<string | undefined> => {
  try {
    const response = await axiosInstance.post(`/exams/${examId}/generate-name`);
    if (!response?.data?.success) {
      console.error(
        'Failed to generate exam name:',
        response?.data?.error || 'Unknown error'
      );

      return undefined;
    }
    const name = response?.data?.data?.name as string | undefined;
    if (!name || typeof name !== 'string') {
      console.error('Generate name response missing name field');
      return undefined;
    }
    return name;
  } catch (error) {
    console.error('Unexpected error while generating exam name:', error);
    return undefined;
  }
};
